import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Save, Check, Lock, Trophy } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const statusColors = {
    'offen': 'bg-green-100 text-green-800',
    'geschlossen': 'bg-yellow-100 text-yellow-800',
    'ausgewertet': 'bg-blue-100 text-blue-800'
};

const statusLabels = {
    'offen': 'Offen',
    'geschlossen': 'Geschl.',
    'ausgewertet': 'Fertig'
};

// Convert UTC time to local time based on timezone  
const convertFromUTC = (utcTime, timezone) => {
    if (!utcTime) return '';
    const [hours, minutes] = utcTime.split(':').map(Number);
    
    // Create a UTC date
    const today = new Date();
    const utcDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes));
    
    // Convert to local timezone
    const localStr = utcDate.toLocaleTimeString('de-CH', { 
        timeZone: timezone || 'Europe/Zurich', 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
    });
    
    return localStr;
};

export default function MatchesPage() {
    const { api } = useAuth();
    const [matches, setMatches] = useState([]);
    const [predictions, setPredictions] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [inputs, setInputs] = useState({});
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        fetchData();
    }, [api]);

    const fetchData = async () => {
        try {
            const [matchesRes, predictionsRes] = await Promise.all([
                api().get('/matches'),
                api().get('/predictions')
            ]);
            
            // Sort matches by date
            const sortedMatches = matchesRes.data.sort((a, b) => 
                new Date(a.match_date) - new Date(b.match_date)
            );
            setMatches(sortedMatches);
            
            // Map predictions by match_id
            const predMap = {};
            predictionsRes.data.forEach(pred => {
                predMap[pred.match_id] = pred;
            });
            setPredictions(predMap);
            
            // Initialize inputs from existing predictions
            const initialInputs = {};
            sortedMatches.forEach(match => {
                const pred = predMap[match.id];
                if (pred) {
                    initialInputs[match.id] = {
                        home: pred.home_score.toString(),
                        away: pred.away_score.toString()
                    };
                }
            });
            setInputs(initialInputs);
            setHasChanges(false);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (matchId, field, value) => {
        // Only allow numbers 0-99
        if (value !== '' && (!/^\d+$/.test(value) || parseInt(value) > 99)) {
            return;
        }
        
        setInputs(prev => ({
            ...prev,
            [matchId]: {
                ...prev[matchId],
                [field]: value
            }
        }));
        setHasChanges(true);
    };

    const handleSaveAll = async () => {
        setSaving(true);
        let savedCount = 0;
        let errorCount = 0;

        for (const match of matches) {
            if (match.status !== 'offen') continue;
            
            const input = inputs[match.id];
            if (!input || input.home === '' || input.away === '' || input.home === undefined || input.away === undefined) {
                continue;
            }

            const homeScore = parseInt(input.home);
            const awayScore = parseInt(input.away);

            if (isNaN(homeScore) || isNaN(awayScore)) continue;

            // Check if this is different from existing prediction
            const existing = predictions[match.id];
            if (existing && existing.home_score === homeScore && existing.away_score === awayScore) {
                continue; // No change
            }

            try {
                if (existing) {
                    await api().put(`/predictions/${existing.id}`, {
                        match_id: match.id,
                        home_score: homeScore,
                        away_score: awayScore
                    });
                } else {
                    await api().post('/predictions', {
                        match_id: match.id,
                        home_score: homeScore,
                        away_score: awayScore
                    });
                }
                savedCount++;
            } catch (error) {
                console.error(`Error saving prediction for match ${match.id}:`, error);
                errorCount++;
            }
        }

        setSaving(false);

        if (savedCount > 0) {
            toast.success(`${savedCount} Tipp(s) gespeichert!`);
            fetchData();
        } else if (errorCount > 0) {
            toast.error(`${errorCount} Fehler beim Speichern`);
        } else {
            toast.info('Keine Änderungen zu speichern');
        }
        
        setHasChanges(false);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="spinner" />
            </div>
        );
    }

    const openMatches = matches.filter(m => m.status === 'offen');
    const closedMatches = matches.filter(m => m.status === 'geschlossen');
    const finishedMatches = matches.filter(m => m.status === 'ausgewertet');

    return (
        <div className="space-y-4 animate-fade-in" data-testid="matches-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-heading">Spiele & Tipps</h1>
                    <p className="text-sm text-muted-foreground">SC Brühl St. Gallen</p>
                </div>
                <Button 
                    onClick={handleSaveAll} 
                    disabled={saving || !hasChanges}
                    className="btn-primary"
                    data-testid="save-all-button"
                >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Speichere...' : 'Alle speichern'}
                </Button>
            </div>

            {/* Open Matches - Can tip */}
            {openMatches.length > 0 && (
                <Card data-testid="open-matches-table">
                    <CardHeader className="py-3 bg-green-50 dark:bg-green-950">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-600" />
                            Offene Spiele ({openMatches.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-20 text-xs">Datum</TableHead>
                                    <TableHead className="text-xs">Heim</TableHead>
                                    <TableHead className="text-xs">Gast</TableHead>
                                    <TableHead className="w-32 text-center text-xs">Dein Tipp</TableHead>
                                    <TableHead className="w-16 text-center text-xs">Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {openMatches.map(match => {
                                    const input = inputs[match.id] || {};
                                    const hasPrediction = predictions[match.id];
                                    
                                    return (
                                        <TableRow key={match.id} data-testid={`match-row-${match.id}`}>
                                            <TableCell className="text-xs py-2">
                                                {format(parseISO(match.match_date), 'dd.MM.yy', { locale: de })}
                                                {match.match_time && <span className="text-muted-foreground ml-1">{convertFromUTC(match.match_time, match.match_timezone)}</span>}
                                            </TableCell>
                                            <TableCell className="text-sm py-2 font-medium">
                                                {match.home_team}
                                            </TableCell>
                                            <TableCell className="text-sm py-2 font-medium">
                                                {match.away_team}
                                            </TableCell>
                                            <TableCell className="py-2">
                                                <div className="flex items-center justify-center gap-1">
                                                    <Input
                                                        type="text"
                                                        inputMode="numeric"
                                                        value={input.home ?? ''}
                                                        onChange={(e) => handleInputChange(match.id, 'home', e.target.value)}
                                                        className="w-10 h-8 text-center p-1 text-sm"
                                                        placeholder="-"
                                                        data-testid={`home-input-${match.id}`}
                                                    />
                                                    <span className="text-muted-foreground">:</span>
                                                    <Input
                                                        type="text"
                                                        inputMode="numeric"
                                                        value={input.away ?? ''}
                                                        onChange={(e) => handleInputChange(match.id, 'away', e.target.value)}
                                                        className="w-10 h-8 text-center p-1 text-sm"
                                                        placeholder="-"
                                                        data-testid={`away-input-${match.id}`}
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-center py-2">
                                                {hasPrediction ? (
                                                    <Check className="w-4 h-4 text-green-500 mx-auto" />
                                                ) : (
                                                    <span className="text-muted-foreground text-xs">-</span>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {/* Closed Matches - Cannot tip anymore */}
            {closedMatches.length > 0 && (
                <Card data-testid="closed-matches-table">
                    <CardHeader className="py-3 bg-yellow-50 dark:bg-yellow-950">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Lock className="w-4 h-4 text-yellow-600" />
                            Geschlossene Spiele ({closedMatches.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-20 text-xs">Datum</TableHead>
                                    <TableHead className="text-xs">Heim</TableHead>
                                    <TableHead className="text-xs">Gast</TableHead>
                                    <TableHead className="w-24 text-center text-xs">Dein Tipp</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {closedMatches.map(match => {
                                    const pred = predictions[match.id];
                                    
                                    return (
                                        <TableRow key={match.id} className="bg-muted/30">
                                            <TableCell className="text-xs py-2">
                                                {format(parseISO(match.match_date), 'dd.MM.yy', { locale: de })}
                                                {match.match_time && <span className="text-muted-foreground ml-1">{convertFromUTC(match.match_time, match.match_timezone)}</span>}
                                            </TableCell>
                                            <TableCell className="text-sm py-2">
                                                {match.home_team}
                                            </TableCell>
                                            <TableCell className="text-sm py-2">
                                                {match.away_team}
                                            </TableCell>
                                            <TableCell className="text-center py-2 font-mono">
                                                {pred ? `${pred.home_score}:${pred.away_score}` : '-'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {/* Finished Matches - With results and points */}
            {finishedMatches.length > 0 && (
                <Card data-testid="finished-matches-table">
                    <CardHeader className="py-3 bg-blue-50 dark:bg-blue-950">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Trophy className="w-4 h-4 text-blue-600" />
                            Ausgewertete Spiele ({finishedMatches.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-20 text-xs">Datum</TableHead>
                                    <TableHead className="text-xs">Heim</TableHead>
                                    <TableHead className="text-xs">Gast</TableHead>
                                    <TableHead className="w-20 text-center text-xs">Resultat</TableHead>
                                    <TableHead className="w-20 text-center text-xs">Dein Tipp</TableHead>
                                    <TableHead className="w-16 text-center text-xs">Punkte</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {finishedMatches.map(match => {
                                    const pred = predictions[match.id];
                                    
                                    return (
                                        <TableRow key={match.id}>
                                            <TableCell className="text-xs py-2">
                                                {format(parseISO(match.match_date), 'dd.MM.yy', { locale: de })}
                                                {match.match_time && <span className="text-muted-foreground ml-1">{convertFromUTC(match.match_time, match.match_timezone)}</span>}
                                            </TableCell>
                                            <TableCell className="text-sm py-2">
                                                {match.home_team}
                                            </TableCell>
                                            <TableCell className="text-sm py-2">
                                                {match.away_team}
                                            </TableCell>
                                            <TableCell className="text-center py-2 font-mono font-bold">
                                                {match.home_score}:{match.away_score}
                                            </TableCell>
                                            <TableCell className="text-center py-2 font-mono">
                                                {pred ? `${pred.home_score}:${pred.away_score}` : '-'}
                                            </TableCell>
                                            <TableCell className="text-center py-2">
                                                {pred ? (
                                                    <Badge className={pred.points > 0 ? 'bg-primary text-white' : ''}>
                                                        {pred.points}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-muted-foreground">-</span>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {matches.length === 0 && (
                <Card className="p-8 text-center">
                    <p className="text-muted-foreground">Keine Spiele vorhanden</p>
                </Card>
            )}
        </div>
    );
}
